import TableComponent from "../ui/Table";
import { useState, useEffect, useContext } from "react";
import AuthContext from "../../contexts/auth-context";
import AppContext from "../../contexts/app-context";
import useAxios from "../../hooks/useAxios";
import { urls, app_paths, toastify_status, PAGE_SIZE } from "../config/variables";
import Toastify from "../ui/Toastify";
import { validateInsuranceAndFacilitiesList, validatePricesTable } from "../../Utils/validateHttpResponse";
import { NavLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Stack,
  Pagination,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from "@mui/material";

import DangerousIcon from "@mui/icons-material/Dangerous";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const PricesTable = () => {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const userRole = appCtx.userRole;
  const { operation } = useAxios();

  const [tabValue, setTabValue] = useState("ICD");
  const [tableData, setTableData] = useState([]);
  const [count, setCount] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [tableDataError, setTableDataError] = useState(undefined);

  const [searchValue, setSearchValue] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const handleChangeCurrentPage = (e, v) => setCurrentPage(v);

  const [facilities, setFacilities] = useState(undefined);
  const [selectedFacility, setSelectedFacility] = useState('');

  const tableDataUrl = {
    "facility_admin": {
      "ICD": urls.FACILITY_ICD_PRICES,
      "CPT": urls.FACILITY_CPT_PRICES,
      "Drug": urls.FACILITY_DRUG_PRICES,
    },
    "insurance_admin": {
      "ICD": urls.GET_INSURNACS_ICD_PRICES,
      "CPT": urls.GET_INSURNACS_CPT_PRICES,
      "Drug": urls.GET_INSURNACS_DRUG_PRICES,
    },
  };

  const GetTableData = () => {
    setLoadingData(true);
    setTableDataError(undefined);

    let url = tableDataUrl[userRole][tabValue] + `?page=${currentPage}&page_size=${PAGE_SIZE}`;

    if (searchValue.trim()) {
      if (tabValue === "ICD") url += `&icd_code=${searchValue}`;
      if (tabValue === "CPT") url += `&cpt_code=${searchValue}`;
      if (tabValue === "Drug") url += `&drug_brand_name=${searchValue}`;
    }

    if (userRole === "insurance_admin" && selectedFacility && selectedFacility !== 'All') {
      url += `&facility_id=${selectedFacility}`;
    }

    operation(
        {
          method: 'GET',
          url,
          headers: { Authorization: `token ${authCtx.token}` },
        },
        handleSetTableData,
        () => setTableDataError(true)
      );
  };

  const handleSetTableData = (results) => {
    setLoadingData(false);

    if (validatePricesTable(results)) {
      setTableData(results.results);
      setCount(Math.ceil(results.count / PAGE_SIZE));
    } else {
      Toastify({
        message: 'invalid prices table data response',
        state: toastify_status.ERROR
      });
    }
  };

  useEffect(() => {
    GetTableData();
    // eslint-disable-next-line
  }, [tabValue, selectedFacility, currentPage, isSearch]);

  const displayRows = tableData.map((item) => {
    if (tabValue === "ICD") {
      return {
        code: item.icdcode?.icdcode ?? "",
        shortdescription: item.icdcode?.shortdescription ?? "",
        cost: item.cost,
        facility: item.facility?.name ?? "",
        discount: item.discount,
        discounted_price: item.discounted_price,
      };
    }
    if (tabValue === "CPT") {
      return {
        code: item.cptcode?.cptcode ?? "",
        shortdescription: item.cptcode?.shortdescription ?? "",
        cost: item.cost,
        facility: item.facility?.name ?? "",
        discount: item.discount,
        discounted_price: item.discounted_price,
      };
    }
    if (tabValue === "Drug") {
      return {
        brand_name: item.product_id?.product_brand_name ?? "",
        cost: item.cost,
        facility: item.facility?.name ?? "",
        discount: item.discount,
        discounted_price: item.discounted_price,
      };
    }
    return {};
  });

  const tableHeaders = {
    "ICD": [
      { label: `${tabValue} Code`, key: "code" },
      { label: "Short description", key: "shortdescription" },
      { label: "Cost", key: "cost" },
      ...(userRole === 'insurance_admin' ? [{ label: "Facility", key: "facility" },] :  []),
      { label: "Discounted cost", key: "discounted_price" },
      { label: "Discount", key: "discount" },
    ],
    "CPT": [
      { label: `${tabValue} Code`, key: "code" },
      { label: "Short description", key: "shortdescription" },
      { label: "Cost", key: "cost" },
      ...(userRole === 'insurance_admin' ? [{ label: "Facility", key: "facility" },] :  []),
      { label: "Discounted cost", key: "discounted_price" },
      { label: "Discount", key: "discount" },
    ],
    "Drug": [
      { label: `${tabValue} Brand Name`, key: "brand_name" },
      { label: "Cost", key: "cost" },
      ...(userRole === 'insurance_admin' ? [{ label: "Facility", key: "facility" },] :  []),
      { label: "Discounted cost", key: "discounted_price" },
      { label: "Discount", key: "discount" },
    ],
  };

  const handleChangeTab = (e, newVal) => {
    setTabValue(newVal);
    setSearchValue("");
    setCurrentPage(1);
    setIsSearch(false);
  };

  useEffect(() => {
    if (userRole === 'insurance_admin' && typeof facilities === 'undefined') {
      operation(
        {
          method: 'GET',
          url: urls.LIST_FACILITIES,
          headers: { Authorization: `token ${authCtx.token}` },
        },
        handleSetFacilities,
        () => console.log("Failed to load facilities")
      );
    }
    // eslint-disable-next-line
  }, [userRole, facilities]);

  const handleSetFacilities = (facilitiesList) => {
    if (validateInsuranceAndFacilitiesList(facilitiesList)) {
      setFacilities(facilitiesList);
    } else {
      Toastify({
        message: 'invalid facilities list response',
        state: toastify_status.ERROR
      });
    }
  };

  const handleChangeFacilitySelect = (event) => {
    setSelectedFacility(event.target.value);
    setCurrentPage(1);
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    setIsSearch(true);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setCurrentPage(1);
    setIsSearch(false);
  };

  useEffect(() => {
    if (isSearch && searchValue === '') {
      handleClearSearch();
    }
    // eslint-disable-next-line
  }, [isSearch, searchValue]);

  return (
    <Box sx={{ position: "relative", height: "600px" }}>
      {/* Search + Tabs */}
      <Stack direction="row" sx={{ mb: 0.8, alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>

          {/* Search Input */}
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSearchClick(event);
              }
            }}
            InputProps={{
              startAdornment: (searchValue && (
                  <InputAdornment position="start">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearchClick}>
                    <SearchIcon />
                  </IconButton>

                </InputAdornment>
              ),
            }}
          />

          {userRole === 'facility_admin' && (
            <NavLink to={app_paths.UPLOAD_PRICES_PAGE} style={{ textDecoration: 'none' }}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                border: '1px solid #4ABBA7',
                color: "#4ABBA7",
                borderRadius: 2,
                cursor: "pointer",
                transition: "0.5s",
                marginLeft: 3,
                "&:hover": { backgroundColor: "#4ABBA7", color: '#fff' }
              }}>
                <AddCircleOutlineIcon />
                <Typography sx={{ fontWeight: 500, fontSize: '16px' }}>
                  Upload Prices File
                </Typography>
              </Box>
            </NavLink>
          )}

          {userRole === 'insurance_admin' && facilities && (
            <FormControl sx={{ ml: 3, minWidth: 200 }} size="small">
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedFacility || 'All'}
                label="Facility"
                onChange={handleChangeFacilitySelect}
              >
                <MenuItem value='All'>All</MenuItem>
                {facilities.map((facility) => (
                  <MenuItem key={facility.id} value={facility.id}>{facility.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Tabs value={tabValue} onChange={handleChangeTab} centered>
          <Tab label="ICD" value="ICD" />
          <Tab label="CPT" value="CPT" />
          <Tab label="Drug" value="Drug" />
        </Tabs>
      </Stack>

      {/* Table / Loading / Error */}
      {!tableDataError && tableData.length === 0 && !loadingData ? (
        <Alert icon={<InfoIcon />} severity="info">No data available.</Alert>
      ) : tableDataError ? (
        <Alert icon={<DangerousIcon />} severity="error">Something went wrong.</Alert>
      ) : loadingData ? (
        <Box sx={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ height: "550px" }}>
            <TableComponent
              tableHead={tableHeaders[tabValue].map((h) => (
                <Box key={h.key} sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}>
                  {h.label}
                </Box>
              ))}
              tableBody={displayRows}
              bodyCells={tableHeaders[tabValue].map((h) => h.key)}
                    bodyCellsCustomSymbol={{ cost: '$', discount: '%', discounted_price: '$' }}
                    customStyle={{
                      tableContainer: {
                        maxHeight: '540px',
                        overflowY: "auto"
                      },
                      headerTableCell: (cell) => {
                        return ({
                          textAlign: "center",
                          border: "1px solid #d9d9d9",
                          fontSize: "18px",
                          fontWeight: "500",
                          backgroundColor: "#F5F5F5",
                          padding: "10px 0 5px",
                          width: cell.key === 'shortdescription'
                           ? '35%'
                           : cell.key === 'discount'
                             ? '15%'
                             : cell.key === 'cost'
                             ? '15%'
                             : cell.key === 'discounted_price'
                             ? '15%'
                           : 'auto'
                        })
                      },
                row: {
                  transition: "0.2s",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "#FCFEFE",
                  "&:hover": { backgroundColor: "#f9f9f9" },
                },
                tableCell: {
                  border: "1px solid #d9d9d9",
                  textAlign: "center",
                  padding: "14px",
                  fontWeight: "500",
                },
              }}
            />
          </Box>

          {count > 1 && (
            <Box sx={{
              position: "absolute",
              left: "50%",
              bottom: "-45px",
              transform: "translate(-50%, -50%)",
            }}>
              <Pagination
                page={currentPage}
                count={count}
                variant="outlined"
                size="medium"
                onChange={handleChangeCurrentPage}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PricesTable;
